import React, { useState, useEffect } from 'react';
import { Search, MapPin, Star, Percent, X, Crosshair, ChevronRight, Compass, Umbrella, Mountain, Landmark } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DefaultSEO from '../components/DefaultSEO';

// Reverse geocode latitude/longitude using Nominatim API
const getCityFromLatLng = async (latitude, longitude) => {
    try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
        const data = await res.json();
        const cityName = data?.address?.city || data?.address?.town || data?.address?.village || "";
        if (cityName) {
            return cityName;
        }
        throw new Error("City name not found in Nominatim response");
    } catch (error) {
        console.error("Nominatim reverse geocoding failed:", error);
        throw error;
    }
};

const ExplorePage = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [filteredCities, setFilteredCities] = useState([]);
    const [specialOffer, setSpecialOffer] = useState(null);

    useEffect(() => {
        const fetchOffer = async () => {
            try {
                const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1';
                const res = await fetch(`${apiBase}/coupons/featured`);
                const data = await res.json();
                if (data.status && data.data && data.data.length > 0) {
                    setSpecialOffer(data.data[0]);
                }
            } catch (err) {
                console.error("Error fetching offer:", err);
            }
        };
        fetchOffer();
    }, []);

    const categories = [
        { name: 'All', icon: <Compass className="w-4 h-4 mr-1.5" /> },
        { name: 'Trending', icon: <Star className="w-4 h-4 mr-1.5" /> },
        { name: 'Beaches', icon: <Umbrella className="w-4 h-4 mr-1.5" /> },
        { name: 'Mountains', icon: <Mountain className="w-4 h-4 mr-1.5" /> },
        { name: 'Heritage', icon: <Landmark className="w-4 h-4 mr-1.5" /> },
        { name: 'Nature', icon: <MapPin className="w-4 h-4 mr-1.5" /> }
    ];

    // Function to calculate city rating based on various factors
    const calculateCityRating = (avgHotelRating, infrastructure, valueForMoney, customerSatisfaction) => {
        const rating = (avgHotelRating * 0.4) + (infrastructure * 0.25) + (valueForMoney * 0.2) + (customerSatisfaction * 0.15);
        return Math.round(rating * 10) / 10;
    };

    // Sample cities data with calculated ratings and categories
    const cities = [
        {
            id: 1,
            name: 'Mumbai',
            area: 'Maharashtra',
            hotels: 245,
            category: 'Trending',
            avgHotelRating: 4.2,
            infrastructure: 4.5,
            valueForMoney: 4.0,
            customerSatisfaction: 4.4,
            image: 'https://images.unsplash.com/photo-1595658658481-d53d3f999875?w=600&h=800&fit=crop'
        },
        {
            id: 2,
            name: 'Delhi',
            area: 'National Capital Territory',
            hotels: 312,
            category: 'Heritage',
            avgHotelRating: 4.0,
            infrastructure: 4.3,
            valueForMoney: 3.8,
            customerSatisfaction: 4.2,
            image: 'https://images.unsplash.com/photo-1586500036706-41963de24d8b?w=600&h=800&fit=crop'
        },
        {
            id: 3,
            name: 'Bangalore',
            area: 'Karnataka',
            hotels: 189,
            category: 'Trending',
            avgHotelRating: 4.3,
            infrastructure: 4.6,
            valueForMoney: 4.2,
            customerSatisfaction: 4.5,
            image: 'https://images.unsplash.com/photo-1596176530529-78163a4f7af2?w=600&h=800&fit=crop'
        },
        {
            id: 8,
            name: 'Jaipur',
            area: 'Rajasthan',
            hotels: 76,
            category: 'Heritage',
            avgHotelRating: 4.4,
            infrastructure: 4.6,
            valueForMoney: 4.6,
            customerSatisfaction: 4.5,
            image: 'https://images.unsplash.com/photo-1477587458883-47145ed94245?w=600&h=800&fit=crop'
        },
        {
            id: 9,
            name: 'Goa',
            area: 'Goa',
            hotels: 143,
            category: 'Beaches',
            avgHotelRating: 4.5,
            infrastructure: 4.7,
            valueForMoney: 4.7,
            customerSatisfaction: 4.8,
            image: 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=600&h=800&fit=crop'
        },
        {
            id: 10,
            name: 'Kerala',
            area: 'Kerala',
            hotels: 112,
            category: 'Nature',
            avgHotelRating: 4.6,
            infrastructure: 4.8,
            valueForMoney: 4.8,
            customerSatisfaction: 4.9,
            image: 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=600&h=800&fit=crop'
        },
        {
            id: 11,
            name: 'Manali',
            area: 'Himachal Pradesh',
            hotels: 120,
            category: 'Mountains',
            avgHotelRating: 4.3,
            infrastructure: 4.0,
            valueForMoney: 4.5,
            customerSatisfaction: 4.6,
            image: 'https://images.unsplash.com/photo-1605649487212-47bdab064df7?w=600&h=800&fit=crop'
        },
        {
            id: 4,
            name: 'Chennai',
            area: 'Tamil Nadu',
            hotels: 156,
            category: 'Heritage',
            avgHotelRating: 4.1,
            infrastructure: 4.2,
            valueForMoney: 4.3,
            customerSatisfaction: 4.0,
            image: 'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=600&h=800&fit=crop'
        }
    ].map(city => ({
        ...city,
        rating: calculateCityRating(city.avgHotelRating, city.infrastructure, city.valueForMoney, city.customerSatisfaction)
    }));

    // Check if device is mobile and redirect if desktop
    useEffect(() => {
        const isMobile = window.innerWidth <= 768;
        if (!isMobile) {
            navigate('/');
        }
    }, [navigate]);

    // Filter cities based on search query and category
    useEffect(() => {
        let filtered = cities;
        
        if (selectedCategory !== 'All') {
            filtered = filtered.filter(city => city.category === selectedCategory);
        }
        
        if (searchQuery.trim() !== '') {
            filtered = filtered.filter(city =>
                city.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                city.area.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }
        
        setFilteredCities(filtered);
    }, [searchQuery, selectedCategory]);

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
    };

    const clearSearch = () => {
        setSearchQuery('');
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?city=${searchQuery.trim()}`);
        }
    };

    const fetchLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const lat = position.coords.latitude;
                    const lon = position.coords.longitude;
                    
                    try {
                        const cityName = await getCityFromLatLng(lat, lon);
                        if (cityName) {
                            navigate(`/search?city=${cityName}`);
                        } else {
                            navigate('/search');
                        }
                    } catch (error) {
                        navigate('/search');
                    }
                },
                (error) => {
                    alert('Unable to get your location. Please enable location access.');
                }
            );
        } else {
            alert('Geolocation is not supported by this browser.');
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC]">
            <DefaultSEO title="Explore Destinations | ZoyoTrip" description="Explore top destinations, hidden gems, and popular cities across India." />
            {/* Header */}
            <div className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-gray-100 shadow-sm">
                <div className="px-4 py-3">
                    {/* Search Bar */}
                    <form onSubmit={handleSearchSubmit} className="relative group">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 transition-colors group-focus-within:text-blue-500" />
                        <input
                            type="text"
                            placeholder="Where do you want to go?"
                            value={searchQuery}
                            onChange={handleSearch}
                            className="w-full !pl-11 !pr-20 py-3.5 bg-gray-100/80 border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:bg-white transition-all shadow-inner text-sm font-medium text-gray-800 placeholder-gray-400"
                        />
                        {/* Right side icons */}
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                            {searchQuery && (
                                <button type="button" onClick={clearSearch} className="p-1.5 bg-white shadow-sm hover:bg-gray-50 rounded-full transition-colors duration-200" aria-label="Clear search">
                                    <X className="w-4 h-4 text-gray-500" />
                                </button>
                            )}
                            <button type="button" onClick={fetchLocation} className="p-1.5 bg-blue-50 hover:bg-blue-100 rounded-full transition-colors duration-200" aria-label="Use current location">
                                <Crosshair className="w-4 h-4 text-blue-600" />
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Categories */}
            <div className="px-4 pt-4 pb-2 overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                <div className="flex space-x-2 pb-1" style={{ WebkitOverflowScrolling: 'touch' }}>
                    {categories.map((cat) => (
                        <button
                            key={cat.name}
                            onClick={() => setSelectedCategory(cat.name)}
                            className={`flex items-center whitespace-nowrap px-4 py-2.5 rounded-2xl text-sm font-semibold transition-all duration-300 ${
                                selectedCategory === cat.name 
                                    ? 'bg-blue-600 text-white shadow-md shadow-blue-200/50 scale-105' 
                                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                            }`}
                        >
                            <span className={selectedCategory === cat.name ? 'text-white' : 'text-gray-400'}>
                                {cat.icon}
                            </span>
                            {cat.name}
                        </button>
                    ))}
                </div>
            </div>

            {/* Offer Banner */}
            {specialOffer && (
                <div className="py-3">
                    <div 
                        className="bg-gradient-to-br from-indigo-900 via-blue-800 to-blue-900 p-5 text-white relative overflow-hidden shadow-lg shadow-blue-900/20 cursor-pointer hover:shadow-blue-900/40 transition-shadow duration-300"
                        onClick={() => navigate(`/offers/${specialOffer.id}`)}
                    >
                        <div className="relative z-10">
                            <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-xs font-semibold mb-3">
                                <Percent className="w-3.5 h-3.5 mr-1" />
                                Special Offer
                            </div>
                            <h3 className="font-bold text-xl mb-1 text-transparent bg-clip-text bg-gradient-to-r from-white to-blue-200">{specialOffer.title}</h3>
                            <p className="text-xs text-blue-100/80 mb-4 max-w-[200px]" dangerouslySetInnerHTML={{ __html: specialOffer.description.substring(0, 80) + '...' }}></p>
                            <button className="text-xs font-bold bg-white text-blue-900 px-4 py-2 rounded-xl shadow-sm hover:scale-105 transition-transform flex items-center">
                                Claim Now <ChevronRight className="w-3.5 h-3.5 ml-1" />
                            </button>
                        </div>
                        {/* Decorative elements */}
                        <div className="absolute -right-6 -top-6 w-32 h-32 bg-blue-500/30 rounded-full blur-2xl"></div>
                        <div className="absolute right-8 -bottom-8 w-24 h-24 bg-purple-500/20 rounded-full blur-xl"></div>
                        <div className="absolute right-0 bottom-0 opacity-10 transform translate-x-4 translate-y-4 pointer-events-none">
                            <Compass className="w-32 h-32" />
                        </div>
                    </div>
                </div>
            )}

            {/* Cities List */}
            <div className="px-4 pb-[90px]">
                <div className="flex justify-between items-end mb-4 mt-2">
                    <h2 className="text-xl font-bold text-gray-900">
                        {searchQuery ? `Results (${filteredCities.length})` : selectedCategory !== 'All' ? `${selectedCategory} Destinations` : 'Top Destinations'}
                    </h2>
                </div>

                {filteredCities.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-3xl border border-gray-100 shadow-sm mt-4">
                        <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                            <MapPin className="w-8 h-8 text-gray-300" />
                        </div>
                        <p className="text-gray-900 font-semibold mb-1">No destinations found</p>
                        <p className="text-sm text-gray-400">Try adjusting your filters or search query.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-4">
                        {filteredCities.map((city, index) => (
                            <div
                                key={city.id}
                                className={`group relative rounded-3xl overflow-hidden cursor-pointer shadow-sm active:scale-95 transition-all duration-300 ${index % 3 === 0 ? 'col-span-2 aspect-[2/1]' : 'aspect-[4/5]'}`}
                                onClick={() => navigate(`/search?city=${city.name}`)}
                            >
                                <img
                                    src={city.image}
                                    alt={city.name}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    loading="lazy"
                                />
                                {/* Gradient Overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                                
                                {/* Rating Badge */}
                                <div className="absolute top-3 right-3 bg-white/20 backdrop-blur-md border border-white/20 text-white px-2 py-1 rounded-lg flex items-center shadow-sm">
                                    <Star className="w-3 h-3 text-yellow-400 fill-yellow-400 mr-1" />
                                    <span className="text-xs font-bold">{city.rating}</span>
                                </div>

                                {/* Content */}
                                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                                    <h3 className="font-bold text-lg leading-tight mb-0.5">{city.name}</h3>
                                    <div className="flex items-center text-xs text-white/80">
                                        <MapPin className="w-3 h-3 mr-1 opacity-70" />
                                        {city.area}
                                    </div>
                                    <div className="mt-2 text-[10px] font-semibold bg-white/20 backdrop-blur-sm border border-white/20 inline-block px-2 py-1 rounded-md">
                                        {city.hotels} Hotels
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            
            <style dangerouslySetInnerHTML={{ __html: `
                .no-scrollbar::-webkit-scrollbar {
                    display: none;
                }
            `}} />
        </div>
    );
};

export default ExplorePage;